import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Separator,
  Tooltip
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_TAI_TMS_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type EnableTaiTMSAppInput = {
  apiKey: string
  appType: AppTypeEnum
}

const formSchema = z.object({
  apiKey: z.string().min(1, 'API Key is required')
})

/**
 * This is the modal for the Tai TMS app.
 */
export function TaiTMSModal({ isOpen, onClose, appType, app, isModalEditable, setModalEditable }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')
  const [generatedApiKey, setGeneratedApiKey] = useState('')
  const [copied, setCopied] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      apiKey: ''
    },
    resolver: zodResolver(formSchema)
  })

  useEffect(() => {
    if (isModalEditable && app?.workspaceApp?.appCredentials?.apiKey) {
      form.reset({
        apiKey: app.workspaceApp.appCredentials.apiKey
      })
    }
  }, [isModalEditable, app, form])

  const [enableTaiTMSApp, { loading }] = useMutation<{ enableTaiTMSApp: { value: string } }, EnableTaiTMSAppInput>(
    ENABLE_TAI_TMS_APP,
    {
      context: {
        headers: {
          'ws-id': workspaceID
        }
      },
      onCompleted: (result) => {
        setGeneratedApiKey(result.enableTaiTMSApp.value)
        toast.success(isModalEditable ? 'Tai TMS app updated successfully!' : 'Tai TMS app enabled successfully!')
        if (isModalEditable && setModalEditable) {
          setModalEditable(false)
        }
      },
      onError: (err) => {
        const errorMessage = `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        setFormError(errorMessage)
        toast.error(errorMessage)
      },
      refetchQueries: [GET_APPS]
    }
  )

  const handleClose = () => {
    setFormError('')
    form.reset()
    onClose()
    if (isModalEditable && setModalEditable) {
      setModalEditable(false)
    }
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    enableTaiTMSApp({
      variables: {
        apiKey: values.apiKey,
        appType: appType as AppTypeEnum
      }
    })
  }

  const displayedApiKey = generatedApiKey || app?.workspaceApp?.appCredentials?.chaineAPIToken

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md w-full">
        <DialogHeader>
          <DialogTitle>{`${isModalEditable ? 'Update' : 'Enable'} Tai TMS for Chain`}</DialogTitle>
          <DialogDescription>
            {`${isModalEditable ? 'Update your' : 'Enabling this integration requires an'} Tai Public API Key.`}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-6 w-full">
            <FormField
              control={form.control}
              name="apiKey"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Tai Public API Key</FormLabel>
                  <FormControl>
                    <Input
                      className="w-full"
                      placeholder="Enter your Tai Public API Key"
                      {...field}
                      disabled={loading}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!!formError && <Alert variant="error" description={formError} />}

            {!!displayedApiKey && (
              <>
                <Separator className="my-4" />
                <div className="space-y-2 w-full">
                  <p className="text-sm font-medium">Chain API Token (for Tai to use):</p>
                  <div className="relative w-full">
                    <div className="rounded-md border bg-muted px-3 py-2 font-mono text-sm break-all">
                      {displayedApiKey}
                    </div>
                    <Tooltip content={copied ? 'Copied!' : 'Copy API key'} side="top" align="center">
                      <p
                        onClick={(e) => {
                          e.stopPropagation()
                          navigator.clipboard.writeText(displayedApiKey)
                          setCopied(true)
                        }}
                      >
                        {copied ? 'Copied!' : 'Copy API key'}
                      </p>
                    </Tooltip>
                  </div>
                </div>
                <Separator className="my-4" />
              </>
            )}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              <a
                href="https://help.trychain.com/en/collections/11885015-tai-tms"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                Read the integration guide
                <ExternalLink className="h-4 w-4" />
              </a>

              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} onClick={form.handleSubmit(onSubmit)}>
                  {loading ? (isModalEditable ? 'Updating...' : 'Enabling...') : isModalEditable ? 'Update' : 'Enable'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
